import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Paper,
} from '@mui/material';
import { useWeb3React } from '@web3-react/core';
import { getContract } from '../config/contracts';
import { ethers } from 'ethers';

interface PermissionInfo {
  address: string;
  isWhitelisted: boolean;
  hasReadPermission: boolean;
  hasWritePermission: boolean;
  permission: number;
  exists: boolean;
  addedAt: number;
}

const PermissionCheck: React.FC = () => {
  const { provider, chainId, account } = useWeb3React();
  const [checkAddress, setCheckAddress] = useState('0xBA0B1CeC8a6719A1E940265a6D897d6FCF7e7815');
  const [permissionInfo, setPermissionInfo] = useState<PermissionInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contractAddress, setContractAddress] = useState('');
  const [contractOwner, setContractOwner] = useState('');

  // Get contract information
  useEffect(() => {
    if (chainId && provider) {
      try {
        const contract = getContract(chainId, provider);
        setContractAddress(contract.address);
        
        // Get contract owner
        contract.owner().then((owner: string) => {
          setContractOwner(owner);
        }).catch((err: any) => {
          console.error('Failed to get contract owner:', err);
        });
      } catch (error) {
        console.error('Failed to get contract:', error);
        setError('Contract not deployed to current network');
      }
    }
  }, [chainId, provider]);

  const checkPermissions = async (retryCount = 0) => {
    if (!checkAddress.trim()) {
      setError('Please enter an address to check');
      return;
    }
    
    if (!provider || !chainId) {
      setError('Please connect wallet first');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const contract = getContract(chainId, provider);
      
      // Check address format
      if (!ethers.utils.isAddress(checkAddress)) {
        throw new Error('Invalid address format');
      }
      
      // Get permission information in parallel
      const [isWhitelisted, hasReadPermission, hasWritePermission, permission] = await Promise.all([
        contract.isWhitelisted(checkAddress),
        contract.hasReadPermission(checkAddress),
        contract.hasWritePermission(checkAddress),
        contract.getPermission(checkAddress)
      ]);
      
      setPermissionInfo({
        address: checkAddress,
        isWhitelisted,
        hasReadPermission,
        hasWritePermission,
        permission: typeof permission === 'object' && permission.toNumber ? permission.toNumber() : Number(permission),
        exists: isWhitelisted,
        addedAt: 0 // No timestamp information in ArtistERC721WithWhitelist contract
      });
      
    } catch (error: any) {
      console.error('Failed to check permissions:', error);
      
      // Check if it's a missing trie node error
      const errorMessage = error.message || error.toString();
      const isMissingTrieNode = errorMessage.includes('missing trie node') || 
                               errorMessage.includes('state is not available') ||
                               errorMessage.includes('Internal JSON-RPC error');
      
      if (isMissingTrieNode && retryCount < 3) {
        console.log(`Detected blockchain node sync issue, retrying... (${retryCount + 1}/3)`);
        // Wait before retrying
        setTimeout(() => {
          checkPermissions(retryCount + 1);
        }, 1000 * (retryCount + 1)); // Incremental delay
        return;
      }
      
      // Handle permission-related errors
      if (errorMessage.includes('insufficient read permission') || 
          errorMessage.includes('ArtistERC721: insufficient read permission')) {
        setError('You do not have read permission. Please contact admin to add your address to whitelist');
      } else if (isMissingTrieNode) {
        setError('Blockchain node sync issue. Please try again later or contact technical support');
      } else if (errorMessage.includes('CALL_EXCEPTION')) {
        setError('Contract call failed. Could be network issue or wrong contract address. Please check network connection');
      } else {
        setError(`Failed to check permissions: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const getPermissionText = (permission: number) => {
    switch (permission) {
      case 0: return 'NONE (No Permission)';
      case 1: return 'READ_ONLY (Read Permission)';
      case 2: return 'READ_WRITE (Read Write Permission)';
      default: return `Unknown Permission (${permission})`;
    }
  };

  const getPermissionColor = (permission: number) => {
    switch (permission) {
      case 0: return 'error';
      case 1: return 'warning';
      case 2: return 'success';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Permission Check Tool
      </Typography>
      
      <Grid container spacing={3}>
        {/* Contract Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contract Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contract Address: {contractAddress || 'Not Retrieved'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contract Owner: {contractOwner || 'Not Retrieved'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Connected Account: {account || 'Not Connected'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Network ID: {chainId || 'Unknown'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Permission Check */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Check Address Permissions
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Address to Check"
                  value={checkAddress}
                  onChange={(e) => setCheckAddress(e.target.value)}
                  placeholder="Enter Ethereum address"
                />
                <Button
                  variant="contained"
                  onClick={() => checkPermissions()}
                  disabled={loading || !checkAddress}
                  sx={{ minWidth: 120 }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Check Permissions'}
                </Button>
              </Box>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {permissionInfo && (
                <Paper sx={{ p: 2, mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Permission Check Results
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Address: {permissionInfo.address}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">In Whitelist:</Typography>
                        <Chip 
                          label={permissionInfo.isWhitelisted ? 'Yes' : 'No'} 
                          color={permissionInfo.isWhitelisted ? 'success' : 'error'}
                          size="small"
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">Read Permission:</Typography>
                        <Chip 
                          label={permissionInfo.hasReadPermission ? 'Yes' : 'No'} 
                          color={permissionInfo.hasReadPermission ? 'success' : 'error'}
                          size="small"
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">Write Permission:</Typography>
                        <Chip 
                          label={permissionInfo.hasWritePermission ? 'Yes' : 'No'} 
                          color={permissionInfo.hasWritePermission ? 'success' : 'error'}
                          size="small"
                        />
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">Permission Level:</Typography>
                        <Chip 
                          label={getPermissionText(permissionInfo.permission)} 
                          color={getPermissionColor(permissionInfo.permission) as any}
                          size="small"
                        />
                      </Box>
                    </Grid>
                    
                    {permissionInfo.addedAt > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Added Time: {new Date(permissionInfo.addedAt * 1000).toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PermissionCheck;